import axios from 'axios'
import { silentSignIn } from '@justeat/f-consumer-oidc'
import { v4 as uuidv4 } from 'uuid'

let _baseUrl = ''
let _authMode
let _tenant

function apiClient(forceSilentSignIn = false) {
  function generateJeDeviceId() {
    const jeUserCookieName = 'je-auser'
    const cookiesArray = document.cookie.split('; ')
    const jeUserCookie = cookiesArray.find(c => c.indexOf(jeUserCookieName) !== -1)

    // If we have a je-auser cookie use it
    if (jeUserCookie) {
      // return value of the cookie
      return jeUserCookie.split('=')[1]
    }
    // This is unlikely to happen, as a user will have a je-auser cookie if they browsed
    // to the ConsumerWeb site before coming to the help centre
    // To satify SmartGateway - generate a random uuid when je-auser is not present
    return `chw-gen-${uuidv4()}`
  }

  const headers = {
    Accept: 'application/json',
    'Accept-Version': process.env.VUE_APP_CHAPI_VERSION,
    'Content-type': 'application/json',
    'x-je-device-id': generateJeDeviceId(),
    'x-je-client-type': 'Web',
    'x-je-client-version': '1'
  }

  function createAxios(authHeaderValue) {
    if (authHeaderValue) {
      headers.Authorization = authHeaderValue
    }

    return axios.create({
      baseURL: _baseUrl,
      headers
    })
  }

  if (_authMode === 'test') {
    if (window.testConsumerId) {
      return Promise.resolve(createAxios(`ConsumerId ${window.testConsumerId}`))
    }
    return Promise.resolve(createAxios())
  }
  const promise = silentSignIn(window.Oidc, null, forceSilentSignIn).then(token => {
    if (token) {
      return createAxios(`Bearer ${token}`)
    }
    return createAxios()
  })

  return promise
}

export default {
  /**
   * Retrieve Article Data from CHAPI
   * Order Id helps CHAPI performance
   * @param {String} articleId Article Id
   * @param {String} orderId Order Id, could be null when landing directly to Article page
   */
  getArticleData(articleId, orderId) {
    const requestQs = orderId ? `?orderId=${orderId}` : ''
    const requestURL = `/applications/consumerhelp/articles/${_tenant}/${articleId}${requestQs}`

    return apiClient()
      .then(client => client.get(requestURL))
      .catch(error => {
        if (error.response && error.response.status === 401) {
          return apiClient(true).then(client => client.get(requestURL))
        }
        throw error
      })
  },
  createSupportRequest(orderId, subject, body) {
    const requestURL = `/applications/consumerhelp/support-requests/${_tenant}`
    const requestBody = {
      OrderId: orderId,
      Subject: subject,
      Body: body
    }

    return apiClient()
      .then(client => client.post(requestURL, requestBody))
      .catch(error => {
        if (error.response && error.response.status === 401) {
          // 401 - Token expired, re-try with new token
          return apiClient(true).then(client => client.post(requestURL, requestBody))
        }
        throw error
      })
  },
  /**
   * Submits Query Support Request Form with
   * @param {String} orderId could be empty string
   * @param {String} queryType selected option query type (<select>)
   * @param {String} body required user message
   * @returns {Object} response data
   */
  createQuerySupportRequest(orderId, queryType, body) {
    const requestURL = `/applications/consumerhelp/support-requests/${_tenant}`
    const requestBody = {
      OrderId: orderId,
      QueryType: queryType,
      Body: body
    }

    return apiClient()
      .then(client => client.post(requestURL, requestBody))
      .catch(error => {
        if (error.response && error.response.status === 401) {
          // 401 - Token expired, re-try with new token
          return apiClient(true).then(client => client.post(requestURL, requestBody))
        }
        throw error
      })
  },
  /**
   * [GET] Contains Page Header, Header Icon, Body and Form inputs
   * @param {Object} routeQueries URL query string
   * @returns {Object} Response data
   */
  // eslint-disable-next-line no-unused-vars
  getContentConcernForm(routeQueries) {
    const queryParams = new URLSearchParams(routeQueries)
    const queryString = queryParams.size > 0 ? `?${queryParams.toString()}` : ''
    const requestURL = `/applications/consumerhelp/support-requests/${_tenant}/report-legal-concern${queryString}`

    return apiClient()
      .then(client => client.get(requestURL))
      .catch(error => {
        window.location = '/help/page-not-found'
        throw error
      })
  },
  /**
   * [POST] Submits Support Request Form data
   * - 400 Invalid Response - Returns the error object
   * @param {String} body ReportContentConcernForm formData
   * @returns {Object} response data
   */
  reportContentConcern(body) {
    const requestURL = `/applications/consumerhelp/support-requests/${_tenant}/report-legal-concern`

    return apiClient()
      .then(client => client.post(requestURL, body))
      .catch(error => {
        throw error
      })
  },
  getChatSupportToken() {
    const requestURL = `/applications/consumerhelp/chatsupporttoken/${_tenant}`

    return apiClient()
      .then(client => client.post(requestURL))
      .catch(error => {
        if (error.response && error.response.status === 401) {
          // 401 - Token expired, re-try with new token
          return apiClient(true).then(client => client.post(requestURL))
        }
        throw error
      })
  },
  getUserToken() {
    if (_authMode === 'test') {
      // wrap response in promise so calling method can always use .then()
      // eslint-disable-next-line no-promise-executor-return
      return new Promise(resolve => resolve(null))
    }
    return silentSignIn(window.Oidc)
  },
  init(baseUrl, authMode, tenant) {
    _baseUrl = baseUrl
    _authMode = authMode
    _tenant = tenant
  }
}
